/**
 * contributionScheduleModel.js — the Contribution Schedule report's pure model
 * (PRD_ContributionSchedule C2, C7, C8, C10, C13, C14).
 *
 * PURE: no jsPDF, no Firestore. Takes the computeContributionSchedule callable's
 * {rows, methods} and returns everything the drawing layer prints — sorted rows
 * with their note markers, the totals row, the "To remit" box, the numbered
 * notes and the footer lines. contributionSchedulePdfService.js only renders it.
 */
import { longMonthLabel, suppressionReason } from '../constants/basisNotes';
import { SSS_BASIS_FACTORS } from '../constants/sssBasisFactors';
import { employeeFullName } from '../utils/employeeSearch';
import { nextMonth } from './contributionBasisReportModel';

export const AGENCY_LABELS = { sss: 'SSS', philhealth: 'PhilHealth', pagibig: 'Pag-IBIG' };

/**
 * C10: the Sort by choices. Last name first — it is the default, and sorts the
 * printed "Last, First M." string. First name sorts "First Last" (founder,
 * /test-impl 2026-11-03); the printed name is the same either way.
 */
export const SORT_OPTIONS = [
  { key: 'employeeName', label: 'Last Name' },
  { key: 'firstLastName', label: 'First Name' },
  { key: 'employeeId', label: 'ID' },
  { key: 'days', label: 'Days' },
  { key: 'basicPay', label: 'Basic Pay' },
];
export const DEFAULT_SORT = { key: 'employeeName', direction: 'asc' };

export const NO_PII_NOTE =
  'Pay details incomplete (salary, bank account or government ID) — payroll skips this employee until they are.';
export const FILING_LINE = 'Informational — not a government filing.';
// PRD_ContributionsStartDate C8 adds the second sentence.
export const BASIC_PAY_LINE =
  "Basic Pay is Rate × Days as printed; it can differ from payroll's basic pay by a few pesos. " +
  "In an employee's contributions-start month, Days and Basic Pay count from that date.";

const round2 = (n) => Math.round(n * 100) / 100;
export const peso = (n) =>
  Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** C3: the fixed reminder, on the page and in the PDF footer. */
export const scheduleReminder = (month) =>
  `Calculated from the time records saved for ${longMonthLabel(month)}. ` +
  'Make sure every time record for that month is complete before relying on these figures.';

/** C2: the subtitle names the attendance month only. */
export const scheduleSubtitle = (month) =>
  `Rate, Days and Basic Pay from ${longMonthLabel(month)} attendance.`;

/**
 * C13: "Last, First M." — the middle name's first letter plus a full stop when
 * there is one; `employeeFullName` when either name is missing.
 */
export const scheduleName = (row) => {
  const first = (row.firstName || '').trim();
  const last = (row.lastName || '').trim();
  if (!first || !last) {
    return employeeFullName({
      personalInfo: { firstName: row.firstName, lastName: row.lastName, fullName: row.fullName },
    });
  }
  const middle = (row.middleName || '').trim();
  return `${last}, ${first}${middle ? ` ${middle[0]}.` : ''}`;
};

/** Days with trailing zeros dropped: 24, 24.75, 26.5. */
export const formatDays = (n) => String(Number(n));

/**
 * C7: one method line per agency. A basis-on line names its factors with their
 * own signs and the runs that deduct it; a saved-amount line makes no month claim.
 */
export const methodLines = (month, methods) => {
  const basisLine = (label, m) => {
    if (!m || !m.enabled) return `${label}: the amount saved on each employee today (contribution basis off).`;
    const factors = SSS_BASIS_FACTORS.filter((f) => m.factors && m.factors[f.key])
      .map((f, i) => (i === 0 ? f.label : `${f.sign === 'subtract' ? '−' : '+'} ${f.label}`))
      .join(' ');
    return (
      `${label}: from ${factors} for ${longMonthLabel(month)} (contribution basis on) — ` +
      `deducted by payroll runs that start in ${longMonthLabel(nextMonth(month))}.`
    );
  };
  return [
    basisLine('SSS', methods && methods.sss),
    basisLine('PhilHealth', methods && methods.philhealth),
    'Pag-IBIG: the amount saved on each employee today.',
    BASIC_PAY_LINE,
  ];
};

/** One agency cell's note as printed text, or null. */
const agencyNoteText = (note, agency, month) => {
  if (!note) return null;
  const label = AGENCY_LABELS[agency];
  if (note.code === 'suppressed') return suppressionReason(note.reason, month, label, note.contributionsStartDate);
  if (note.code === 'no-table') return suppressionReason('no-table', month, label);
  if (note.code === 'unmatched') {
    return (
      `${label} ER: ₱${peso(note.ee)} saved in the employee's ${label} field (Employment) is not an amount ` +
      `in the ${label} table — correct that field.`
    );
  }
  return null;
};

const duplicateNote = (id, n) =>
  `Employee ID ${id} is on ${n} employee records — the same time records are counted on each.`;

const TEXT_KEYS = new Set(['employeeId', 'employeeName', 'firstLastName']);
const tieBreak = (a, b) =>
  String(a.employeeId).localeCompare(String(b.employeeId)) || String(a.docId).localeCompare(String(b.docId));

/**
 * C10: Attendance Summary's rule (text by localeCompare, numbers numerically)
 * plus the two things it lacks — a null (—) sorts after every number in BOTH
 * directions, and ties keep ID order whatever order the rows arrived in.
 */
export const compareScheduleRows = (key, direction) => (a, b) => {
  const av = a[key];
  const bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull || bNull) return aNull === bNull ? tieBreak(a, b) : aNull ? 1 : -1;
  const c = TEXT_KEYS.has(key) ? String(av).localeCompare(String(bv)) : av - bv;
  return (direction === 'desc' ? -c : c) || tieBreak(a, b);
};

const SHARE_COLS = [
  ['sss', 'ee'], ['sss', 'er'], ['sss', 'ec'],
  ['philhealth', 'ee'], ['philhealth', 'er'],
  ['pagibig', 'ee'], ['pagibig', 'er'],
];
const sum = (rows, read) => round2(rows.reduce((s, r) => s + (read(r) ?? 0), 0));

/**
 * Build the printed model.
 *
 * @param {Object} args
 * @param {string} args.month - 'YYYY-MM'
 * @param {Object[]} args.rows - the callable's rows
 * @param {Object} args.methods - the callable's methods
 * @param {{key: string, direction: 'asc'|'desc'}} [args.sort]
 * @returns {{rows: Object[], totals: Object, remit: Object, notes: Object[], footer: string[],
 *   subtitle: string}}
 */
export const buildContributionScheduleModel = ({ month, rows, methods, sort = DEFAULT_SORT }) => {
  const idCounts = new Map();
  rows.forEach((r) => idCounts.set(r.employeeId, (idCounts.get(r.employeeId) || 0) + 1));

  const sorted = rows
    .map((r) => {
      const employeeName = scheduleName(r);
      const firstLast = `${(r.firstName || '').trim()} ${(r.lastName || '').trim()}`.trim();
      return { ...r, employeeName, firstLastName: firstLast || employeeName };
    })
    .sort(compareScheduleRows(sort.key, sort.direction));

  // Notes are numbered by first appearance in PRINTED order and de-duplicated
  // by text; a marker sits after EE (suppression), ER (unmatched / no table) or
  // Name (row-level), C9.
  const notes = new Map();
  const mark = (text) => {
    if (!text) return null;
    if (!notes.has(text)) notes.set(text, notes.size + 1);
    return notes.get(text);
  };

  const printed = sorted.map((r) => {
    const nameMarks = [
      r.noPii ? mark(NO_PII_NOTE) : null,
      idCounts.get(r.employeeId) > 1 ? mark(duplicateNote(r.employeeId, idCounts.get(r.employeeId))) : null,
    ].filter(Boolean);
    const marks = { name: nameMarks };
    ['sss', 'philhealth', 'pagibig'].forEach((agency) => {
      const note = r[agency] && r[agency].note;
      const n = mark(agencyNoteText(note, agency, month));
      if (n) marks[note.code === 'suppressed' ? `${agency}.ee` : `${agency}.er`] = n;
    });
    return { ...r, marks };
  });

  const totals = { basicPay: sum(rows, (r) => r.basicPay) };
  SHARE_COLS.forEach(([agency, k]) => {
    totals[`${agency}.${k}`] = sum(rows, (r) => r[agency] && r[agency][k]);
  });

  const excluded = (agency) => rows.filter((r) => r[agency] && r[agency].er === null).length;
  const remitLine = (agency) => {
    const ee = totals[`${agency}.ee`];
    const er = totals[`${agency}.er`];
    const ec = agency === 'sss' ? totals['sss.ec'] : null;
    const n = excluded(agency);
    return {
      agency,
      label: AGENCY_LABELS[agency],
      ee,
      er,
      ec,
      total: round2(ee + er + (ec || 0)),
      note: n ? `excludes ${n} ${n === 1 ? 'employee' : 'employees'} with no employer share — see notes` : null,
    };
  };
  const lines = ['sss', 'philhealth', 'pagibig'].map(remitLine);

  return {
    subtitle: scheduleSubtitle(month),
    rows: printed,
    totals,
    remit: { lines, grandTotal: round2(lines.reduce((s, l) => s + l.total, 0)) },
    notes: [...notes].map(([text, n]) => ({ n, text })),
    footer: [...methodLines(month, methods), scheduleReminder(month)],
    filingLine: FILING_LINE,
  };
};
